/**
 * Screen-context derivation (UI awareness) — turns the current route into the
 * `screen_context` payload GritClaw consumes so the assistant can resolve "this
 * device" / "these" without re-specification. See screen-context.schema.json.
 *
 * Generic by design: the device-detail case (the marquee "why is this one
 * offline?" flow) is derived from the URL alone, so no page needs to be touched.
 * Pages may still enrich via GritClawProvider's setScreenOverride().
 */
export interface ScreenContext {
  version: 1;
  surface: 'dashboard';
  route: string;
  selection?: Record<string, unknown>;
  filters?: Record<string, unknown>;
  visible_summary?: string;
  captured_at: string;
}

export interface ScreenOverride {
  selection?: Record<string, unknown>;
  filters?: Record<string, unknown>;
  visible_summary?: string;
}

const summarize = (pathname: string): { summary: string; selection?: Record<string, unknown> } => {
  const deviceDetail = pathname.match(/^\/devices\/([^/]+)$/);
  if (deviceDetail) {
    return {
      summary: `Device detail page for ${deviceDetail[1]}`,
      selection: { device_id: deviceDetail[1] },
    };
  }
  const rtlDetail = pathname.match(/^\/rtl\/([^/]+)$/);
  if (rtlDetail && rtlDetail[1] !== 'tolerance-Profiles') {
    return {
      summary: `RTL device data view for ${rtlDetail[1]}`,
      selection: { device_id: rtlDetail[1], device_type: 'rtl' },
    };
  }
  const map: Record<string, string> = {
    '/': 'Dashboard — fleet map overview',
    '/devices': 'Device list',
    '/device-alerts': 'Device alerts list',
    '/shipments': 'Shipments list',
    '/rtl': 'RTL devices list',
    '/locations': 'Locations map',
    '/users': 'Portal users',
    '/settings': 'Account settings',
    '/gritclaw': 'GritClaw assistant chat',
    '/gritclaw/schedules': 'GritClaw scheduled jobs',
  };
  return { summary: map[pathname] ?? `Viewing ${pathname}` };
};

export const deriveScreenContext = (
  pathname: string,
  override?: ScreenOverride
): ScreenContext => {
  const base = summarize(pathname);
  const selection = { ...(base.selection ?? {}), ...(override?.selection ?? {}) };
  return {
    version: 1,
    surface: 'dashboard',
    route: pathname,
    selection: Object.keys(selection).length ? selection : undefined,
    filters: override?.filters,
    visible_summary: (override?.visible_summary ?? base.summary).slice(0, 500),
    captured_at: new Date().toISOString(),
  };
};
